import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_STORAGE_KEY, languages, getSavedLanguage } from '../../i18n';

interface LanguageSelectorProps {
  className?: string;
  onLanguageChange?: (languageCode: string) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  onLanguageChange,
}: LanguageSelectorProps) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(getSavedLanguage().toUpperCase());

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguage(languageCode);
    setIsOpen(false);

    // Save to localStorage
    localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);

    if (onLanguageChange) {
      onLanguageChange(languageCode);
    }
    // Use lowercase version for i18n
    i18n.changeLanguage(languageCode.toLowerCase());
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`language-selector ${className} ${isOpen ? 'language-selector--open' : ''}`}
      onClick={toggleDropdown}
      ref={dropdownRef}
    >
      <button type="button" className="language-selector__toggle" aria-label="Select language">
        <span className="language-selector__current">{selectedLanguage}</span>
      </button>

      {isOpen && (
        <div className="language-selector__dropdown">
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              className={`language-selector__option ${
                selectedLanguage === language.code ? 'language-selector__option--selected' : ''
              }`}
              onClick={() => handleLanguageSelect(language.code)}
            >
              {language.name}
              {selectedLanguage === language.code && <span className="language-selector__check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

LanguageSelector.defaultProps = {
  className: '',
  onLanguageChange: undefined,
};

export default LanguageSelector;
